import {
    Button,
    type ButtonProps,
    Card,
    Icon,
    type IconTypes,
    type MenuItem,
    useDialog,
} from '@ics-portal/guardian';
import { type Maybe } from 'graphql/jsutils/Maybe';
import { LatLng } from 'leaflet';
import { useRef, type ReactNode, useMemo } from 'react';
import { IconEntityMap } from '@components/_atoms/icons';
import { useProjectAccess } from '@pages/project/_utils/contexts/ProjectAccessContext';
import { useProjectSummary } from '@pages/project/_utils/contexts/ProjectSummaryContext';
import { getProjectFormSteps } from '@pages/projects/_utils/components/projectForm/getProjectFormSteps';
import { ProjectFormStepperWrapper } from '@pages/projects/_utils/components/projectForm/ProjectForm';
import { ProjectLocationEditor } from '@pages/projects/_utils/components/ProjectLocationEditor/ProjectLocationEditor';
import { getProjectPath, ProjectPath } from '@utils/getMenuContent';
import { getProjectTypePresentation } from '@utils/projectType';
import { sortBySequence } from '@utils/sorting';
import s from './ProjectInfo.module.scss';

interface InfoElementProps {
    title: string;
    icon: IconTypes;
    label?: string;
    value?: Maybe<string | number>;
    to?: string;
    button?: ReactNode;
    iconPos?: ButtonProps['iconPos'];
    contrast?: boolean;
}

function InfoElement(props: InfoElementProps): ReactNode {
    const { title, icon, value, to, button, contrast, label, iconPos } = props;

    return (
        <div className="flex gap-2 align-items-center">
            <Icon fontSize="normal" className={s.infoElementIcon} name={icon} />
            <div className="flex flex-1 justify-content-between">
                <div className="flex flex-column gap-1">
                    <span className={s.infoElementValue}>{value ?? '-'}</span>
                    <strong className={s.infoElementTitle}>
                        {title.toUpperCase()}
                    </strong>
                </div>
                {to && !button ? (
                    <Button
                        label={label}
                        iconPos={iconPos}
                        contrast={contrast}
                        className={s.redirectButton}
                        link
                        to={to}
                        icon={<Icon name="arrow_right_alt" />}
                        iconSize="sm"
                    />
                ) : null}
                {button}
            </div>
        </div>
    );
}

export function ProjectInfo(): ReactNode {
    const { project } = useProjectSummary();
    const { stepper } = useDialog();
    const { can } = useProjectAccess();
    const isDirtyRef = useRef<() => boolean>(() => false);

    const uniqueMembersCount = project?.participants?.reduce<string[]>(
        (acc, participant) => {
            if (participant && !acc.includes(participant.user.id)) {
                acc.push(participant.user.id);
            }
            return acc;
        },
        [],
    ).length;

    const projectLocation = useMemo(() => {
        const address = [
            project?.locationStreet,
            project?.locationCity,
            project?.locationCountry,
        ]
            .filter(Boolean)
            .join(', ');
        const latLng = [project?.point?.latitude, project?.point?.longitude]
            .filter(Boolean)
            .join(', ');

        return address || latLng || '-';
    }, [
        project?.locationStreet,
        project?.locationCity,
        project?.locationCountry,
        project?.point,
    ]);
    const plantName = project?.plantName ?? '-';
    const location =
        project?.point?.latitude && project.point.longitude
            ? new LatLng(project.point.latitude, project.point.longitude)
            : undefined;

    const projectInfoActions: MenuItem[] = can('project:edit')
        ? [
              {
                  label: 'Edit',
                  icon: <Icon name="edit" />,
                  command: () => {
                      if (!project) {
                          return;
                      }

                      stepper({
                          title: 'Edit project',
                          linear: false,
                          buttons: {
                              submitLabel: 'Update project',
                              submitDisabled: () => !isDirtyRef.current(),
                          },
                          wrapper: ({ children }) => (
                              <ProjectFormStepperWrapper
                                  project={project}
                                  isDirtyRef={isDirtyRef}
                              >
                                  {children}
                              </ProjectFormStepperWrapper>
                          ),
                          steps: getProjectFormSteps(true),
                          confirmClose: {
                              title: 'Discard unsaved changes?',
                              dialogName: 'Edit project',
                              isDirty: () => isDirtyRef.current(),
                          },
                      });
                  },
              },
          ]
        : null;
    const securityResponsibleUser =
        project?.securityResponsible ?? project?.creator;

    const securityResponsible = securityResponsibleUser
        ? `${securityResponsibleUser.firstName} ${securityResponsibleUser.lastName}`
        : '-';

    const icon =
        getProjectTypePresentation({
            label: project?.type?.name,
            typeId: project?.type?.id,
        })?.icon ?? IconEntityMap.ProjectTypeDefault;
    return (
        <Card
            cardClassName={s.projectInfo}
            foldable={false}
            title="Project Info"
            actions={projectInfoActions}
            contrast
            menuProps={{
                buttonProps: {
                    contrast: true,
                },
            }}
        >
            <div className="flex flex-column h-full justify-content-between gap-4 ">
                <InfoElement
                    title="Type"
                    value={project?.type?.name}
                    icon={icon}
                />
                <InfoElement
                    title="Organization"
                    value={project?.organization?.name}
                    icon="corporate_fare"
                />
                <InfoElement
                    title="Customer name"
                    value={project?.customerName}
                    icon="account_box"
                />
                <InfoElement
                    title="Project location"
                    value={projectLocation}
                    icon="distance"
                    button={
                        location ? (
                            <ProjectLocationEditor
                                latLng={location}
                                buttonProps={{
                                    icon: <Icon name="arrow_right_alt" />,
                                    iconPos: 'right',
                                    label: 'View',
                                    link: true,
                                    severity: undefined,
                                    className: s.redirectButton,
                                }}
                            />
                        ) : null
                    }
                />
                <InfoElement
                    title="Plant name"
                    value={plantName}
                    icon="factory"
                />
                <InfoElement
                    title="Project members"
                    value={uniqueMembersCount}
                    icon="group"
                    to={`${getProjectPath(ProjectPath.Settings, project?.id ?? '')}#members`}
                    label="View"
                    iconPos="right"
                />
                <InfoElement
                    title="Security responsible"
                    value={securityResponsible}
                    icon="shield_person"
                />
                {sortBySequence(project?.projectAttributes ?? [], true).map(
                    (attribute) => (
                        <InfoElement
                            icon="user_attributes"
                            key={attribute.id}
                            title={attribute.label}
                            value={attribute.value}
                        />
                    ),
                )}
            </div>
        </Card>
    );
}
